import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { ImageWithFallback } from '../../utils/ImageWithFallback'
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  Leaf,
  MapPin,
  Calendar,
  ThumbsUp,
} from 'lucide-react'
import ImagePlaceholder from '../../utils/ImagePlaceholder'
import { useNavigate } from 'react-router'

import before1 from '../../assets/before-after/Before.png'
import before2 from '../../assets/before-after/Before-1.png'
import before3 from '../../assets/before-after/Before-2.png'
import before4 from '../../assets/before-after/Before-3.png'
import before5 from '../../assets/before-after/Before-4.png'

import after1 from '../../assets/before-after/After.png'
import after2 from '../../assets/before-after/After-1.png'
import after3 from '../../assets/before-after/After-2.png'
import after4 from '../../assets/before-after/After-3.png'
import after5 from '../../assets/before-after/After-4.png'

export function TestimonialsComponent() {
  const navigate = useNavigate()
  const [currentTestimonial, setCurrentTestimonial] = useState(0)

  const testimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      role: 'Homeowner',
      location: 'Beverly Hills, CA',
      image:
        'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      rating: 5,
      date: 'September 2025',
      project: 'Complete Backyard Makeover',
      content:
        'Gardenly Thumb transformed our boring backyard into an absolute paradise! The team was professional, creative, and went above and beyond our expectations. Our new garden is the envy of the neighborhood, and we spend every evening out there now. The attention to detail and quality of work is simply outstanding.',
      beforeImage:
        before1,
      afterImage:
        after1,
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'Business Owner',
      location: 'Santa Monica, CA',
      image:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      rating: 5,
      date: 'September 2025',
      project: 'Commercial Landscaping',
      content:
        'We hired Gardenly Thumb to redesign the landscaping around our office building, and the results exceeded all expectations. The modern, sustainable design perfectly reflects our company values. Our employees love the outdoor spaces, and clients always comment on how beautiful our grounds look. Highly recommended!',
      beforeImage:
        before2,
      afterImage:
        after2,
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      role: 'Property Manager',
      location: 'Pasadena, CA',
      image:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      rating: 5,
      date: 'September 2025',
      project: 'Residential Complex Landscaping',
      content:
        "As a property manager, I've worked with many landscaping companies, but Gardenly Thumb stands out for their reliability, quality, and communication. They maintain our property beautifully and always respond quickly to any requests. The residents are constantly complimenting the grounds.",
      beforeImage:
        before3,
      afterImage:
        after3,
    },
    {
      id: 4,
      name: 'David Thompson',
      role: 'Restaurant Owner',
      location: 'West Hollywood, CA',
      image:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      rating: 5,
      date: 'September 2025',
      project: 'Outdoor Dining Area',
      content:
        'Gardenly Thumb created the most beautiful outdoor dining space for our restaurant. The design is both functional and stunning, creating the perfect ambiance for our guests. Since the renovation, our outdoor seating is always fully booked. The plants they chose are thriving and low-maintenance.',
      beforeImage:
        before4,
      afterImage:
        after4,
    },
    {
      id: 5,
      name: 'Lisa Park',
      role: 'Homeowner',
      location: 'Manhattan Beach, CA',
      image:
        'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      rating: 5,
      date: 'September 2025',
      project: 'Front Yard Redesign',
      content:
        "We wanted to increase our home's curb appeal, and Gardenly Thumb delivered beyond our wildest dreams. The new front yard design is absolutely gorgeous and has increased our property value significantly. Neighbors stop by just to admire the landscaping! The team was professional and completed everything on time.",
      beforeImage:
        before5,
      afterImage:
        after5,
    },
  ]

  const reviews = [
    {
      platform: 'Google Reviews',
      rating: 4.9,
      count: 247,
      icon: '⭐',
    },
    {
      platform: 'Yelp',
      rating: 4.8,
      count: 156,
      icon: '⭐',
    },
    {
      platform: 'Better Business Bureau',
      rating: 'A+',
      count: null,
      icon: '🏆',
    },
    {
      platform: "Angie's List",
      rating: 4.9,
      count: 89,
      icon: '⭐',
    },
  ]

  // Auto-advance testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
    }, 6000)

    return () => clearInterval(timer)
  }, [testimonials.length])

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    )
  }

  const currentReview = testimonials[currentTestimonial]

  return (
    <>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="mb-4 bg-green-100 text-green-800 border-green-200">
              <Leaf className="w-4 h-4 mr-2" />
              Client Testimonials
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our satisfied clients
              have to say about their experience working with Gardenly Thumb.
            </p>
          </motion.div>

          {/* Review Platforms */}
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                className="text-center p-4 bg-gray-50 rounded-xl"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="text-2xl mb-2">{review.icon}</div>
                <div className="font-semibold text-gray-900 mb-1">
                  {review.platform}
                </div>
                <div className="text-lg font-bold text-green-600 mb-1">
                  {review.rating}
                  {typeof review.rating === 'number' && '/5'}
                </div>
                {review.count && (
                  <div className="text-sm text-gray-500">
                    ({review.count} reviews)
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>

          {/* Main Testimonial */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="max-w-6xl mx-auto overflow-hidden shadow-2xl border-0">
                  <div className="grid lg:grid-cols-2">
                    {/* Content */}
                    <CardContent className="p-8 lg:p-12">
                      {/* Quote Icon */}
                      <Quote className="w-12 h-12 text-green-600 mb-6" />

                      {/* Rating */}
                      <div className="flex items-center mb-6">
                        {[...Array(currentReview.rating)].map((_, i) => (
                          <Star
                            key={i}
                            className="w-5 h-5 fill-yellow-400 text-yellow-400 mr-1"
                          />
                        ))}
                        <span className="ml-2 text-gray-600">
                          ({currentReview.rating}.0)
                        </span>
                      </div>

                      {/* Testimonial Content */}
                      <blockquote className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-8">
                        "{currentReview.content}"
                      </blockquote>

                      {/* Client Info */}
                      <div className="flex items-center mb-6">
                        <ImageWithFallback
                          src={currentReview.image}
                          alt={currentReview.name}
                          className="w-16 h-16 rounded-full object-cover mr-4"
                        />

                        <div>
                          <div className="font-semibold text-gray-900">
                            {currentReview.name}
                          </div>
                          <div className="text-gray-600">
                            {currentReview.role}
                          </div>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPin className="w-3 h-3 mr-1" />
                            {currentReview.location}
                          </div>
                        </div>
                      </div>

                      {/* Project Info */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <ThumbsUp className="w-4 h-4 mr-2 text-green-600" />
                          {currentReview.project}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-green-600" />
                          {currentReview.date}
                        </div>
                      </div>
                    </CardContent>

                    {/* Before/After Images */}
                    <div className="relative">
                      <div className="grid grid-cols-2 h-full">
                        <div className="relative">
                          <ImageWithFallback
                            src={currentReview.beforeImage}
                            alt="Before"
                            className="w-full h-full object-cover"
                          />
                          {/* <ImagePlaceholder width={288} height={591} /> */}
                          <div className="absolute bottom-4 left-4">
                            <Badge className="bg-red-600 text-white">
                              Before
                            </Badge>
                          </div>
                        </div>
                        <div className="relative">
                          <ImageWithFallback
                            src={currentReview.afterImage}
                            alt="After"
                            className="w-full h-full object-cover"
                          />
                          {/* <ImagePlaceholder width={288} height={591} /> */}
                          <div className="absolute bottom-4 right-4">
                            <Badge className="bg-green-600 text-white">
                              After
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-center mt-8 space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={prevTestimonial}
                className="w-10 h-10 rounded-full p-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              {/* Dots Indicator */}
              <div className="flex items-center space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${index === currentTestimonial
                      ? 'bg-green-600 w-8'
                      : 'bg-gray-300'
                      }`}
                  />
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={nextTestimonial}
                className="w-10 h-10 rounded-full p-0"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Call to Action */}
          <motion.div
            className="mt-16 text-center bg-green-50 rounded-2xl p-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Join Our Happy Clients?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Experience the Gardenly Thumb difference for yourself. Let's create
              an outdoor space that you'll love and want to show off to everyone.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => navigate('/contact')}
              >
                Start Your Project Today
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/portfolio')}
              >
                View More Projects
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}


